// LessonPage.tsx
import {
  useState,
  useContext,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useGet } from '@/query/fetchHooks';
import { useParams, useNavigate } from 'react-router-dom';
import { NavContext } from '@/components/NavContext';
import { Button, IconButton, TEOLoader } from '@teo/components';
import { Sidebar, Cross } from '@teo/components/icons';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { LessonTree } from '@/components/LessonTree/LessonTree';
import { LessonView } from '@/components/LessonView';
import {
  newUid,
  isEmpty,
  deep_copy,
  useStateAndRef,
  useAsyncState,
  scrollIntoViewWithOffset,
  nthIndexOf,
} from '@/util';
import { config } from '@/config';
import { axios } from '@/axios';
import { XAPIService } from '@/services/xapi.service';
import { ScormService } from '@/services/scorm.service';
import { LtiService } from '@/services/lti.service';
import { TinCanService } from '@/services/tincan.service';
import { useMediaQuery } from 'react-responsive';
import { Userwithorganisationwithidps } from '@/models';
import LessonHeader from './LessonHeader';
import { CourseSuccess } from './CourseSuccess';
import { NotificationContext } from '@/components/Contexts/NotificationContext';
import NotificationMessage from '@/components/NotificationMessage/NotificationMessage';
import i18n from 'i18next';
import IncompleteModal from '@/components/IncompleteModal';
import { ExtraInfoLesson } from './ExtraInfoLesson';
import {
  useContentLinks,
  matchesContentLink,
} from '@/components/useContentLinks';
import { ImageZoom } from '@/components/ImageZoom';
import ScormLessonPage from '../ScormCourse/ScormLessonPage';
import Cmi5, { LaunchParameters } from '@xapi/cmi5';

function LessonPage({ result }: any) {
  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const queryParams = new URLSearchParams(window.location.search);
  const prevPage = queryParams.get('details');
  const qr_share = queryParams.get('qr_code');
  const authUser = auth?.user as Userwithorganisationwithidps;

  const { setNavDisabled } = useContext(NavContext);
  const { courseId, lessonId, organisationId } = useParams();

  const divRef = useRef<HTMLDivElement>(null);

  let { studentId } = useParams();

  let itSMe = false;
  if (!studentId) {
    itSMe = true;
    studentId = authUser?.id as unknown as string;
  }
  const isTeacher = !itSMe;

  const navigate = useNavigate();
  const [course, setCourse] = useState<any>(null);

  const [student, setstudent] = useState<any>(null);
  const [activeLesson, setActiveLesson, activeLessonRef] = useStateAndRef(null);
  const [lastTickTime, setLastTickTime] = useState<any>(new Date());
  const [isScormScourse, setIsScormScourse] = useState<boolean>(false);

  const isPreview =
    window.location.pathname.includes('/preview/') || !studentId;
  const [statuses, setStatuses, statusRef] = useStateAndRef(null);
  const [statusesAssets, setStatusesAssets, statusesAssetsRef] =
    useStateAndRef(null);
  const [showCourseSuccess, setShowCourseSuccess] = useState(false);

  const expectAStudent = !isPreview;
  const expectAStatus = expectAStudent && !isTeacher;

  const [loaded, setLoaded, setLoadedRef] = useStateAndRef(null);
  const [nextLesson, setNextLesson] = useState<any>(null);
  const [previousLesson, setPreviousLesson] = useState<any>(null);

  const [hideSidebar, setHideSidebar] = useState<boolean>(false);
  const [hideContent, setHideContent] = useState<boolean>(false);
  const [submitFn, setSubmitFn, submitFnRef] = useStateAndRef(undefined);
  const [scrollPercentage, setScrollPercentage] = useState(0);
  const [nrOfPages, setNrOfPages] = useState(1);
  const [page, setPage] = useState(0);

  const [loadeContent, setLoadeContent] = useState(false);

  const [isIncomplete, setIsIncomplete] = useState(false);
  const [incompleteWidgets, setIncompleteWidgets] = useState(null);

  const [allExtraInfo, setAllExtraInfo] = useState(null);
  const [rootCourseId, setRootCourseId] = useState(null);
  const [allDirectories, setAllDirectories] = useState(null);
  const [allAssets, setAllAssets] = useState(null);

  const [showExtraInfo, setShowExtraInfo] = useState(false);
  const [extraInfoFullScreen, setExtraInfoFullScreen] = useState(false);
  const [activeExtraInfo, setActiveExtraInfo] = useState(null);
  const [lengthExtraInfo, setLengthExtraInfo] = useState(0);
  const [numLesAs, setNumLesAs] = useState<any>(0);
  const [hasWidgets, setHasWidgets] = useState<boolean>(false);
  const [isLessonLoaded, setIsLessonLoaded] = useState(true);
  const [isLessonLoadedSub, setIsLessonLoadedSub] = useState(false);

  const [imageZoom, setImageZoom] = useState(false);
  const [refTree, setRefTree] = useState(newUid(10));

  const [courseExpireRunning, setCourseExpireRunning] = useState(false);
  const [lessonExpireRunning, setLessonExpireRunning] = useState(false);

  const basePath = window.location.pathname;
  const coursePath = isTeacher
    ? `/${i18n.language}/follow-up/${organisationId}/courses/details/${courseId}`
    : basePath.slice(0, basePath.lastIndexOf('/lesson'));

  const { notification, setShowNotification } = useContext(NotificationContext);
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
  const iSsmallLaptop = useMediaQuery({ query: '(max-width: 1199px)' });

  // --- Course timer latch (authoritative expiry comes from LessonHeader Timer onExpire) ---
  const timedCourse = !!course?.courseTimer;
  const [courseExpiredLocal, setCourseExpiredLocal] = useState(false);

  // --- Lesson timer latch (authoritative expiry comes from LessonHeader Timer onExpire) ---
  const timedLesson = (activeLesson as any)?.timed === 'limited';
  const [lessonExpiredLocal, setLessonExpiredLocal] = useState(false);

  const didImmediateTickRef = useRef(false);

  // Reset local-expired latch when timer identity changes (e.g. resetCourseForUser)
  useEffect(() => {
    setCourseExpiredLocal(false);
  }, [courseId, student?.courseTimerStartedAt, course?.courseTimer]);

  // Reset lesson-expired latch when lesson identity changes
  useEffect(() => {
    setLessonExpiredLocal(false);
    didImmediateTickRef.current = false;
  }, [(activeLesson as any)?.lessonId, (activeLesson as any)?.version]);

  // Optional: expire immediately on load if BE says it's already expired
  const courseAlreadyExpired = useMemo(() => {
    if (!timedCourse) return false;

    const totalSeconds = Number(course?.courseTimer ?? 0);
    const startedAtStr = student?.courseTimerStartedAt;
    const dbNowStr = student?.dbNow;
    if (!startedAtStr || !dbNowStr) return false;

    const startedAtMs = Date.parse(startedAtStr);
    const dbNowMs = Date.parse(dbNowStr);
    if (!Number.isFinite(startedAtMs) || !Number.isFinite(dbNowMs))
      return false;

    return dbNowMs >= startedAtMs + totalSeconds * 1000;
  }, [
    timedCourse,
    course?.courseTimer,
    student?.courseTimerStartedAt,
    student?.dbNow,
  ]);

  const courseTimerExpired =
    (timedCourse && courseAlreadyExpired) || courseExpiredLocal;

  const lessonTimerExpired = timedLesson && lessonExpiredLocal;
  const blockForLessonTimer =
    timedLesson && !lessonTimerExpired && !isTeacher && !isPreview;

  const activeStatus = useRef<any>(null);
  const activeStatusAsset = useRef<any>(null);

  //this part registers time, it reports the time spent in ticks every 60s or 5s depending
  //on whether a lesson is explicitly timed or not. When leavind a lesson the difference
  //between lastTickTime and Date.now() is added;
  //started by timetickTimerReset.current()
  //stopped by clearInterval(timetickTimer.current)
  const timetickTimer: any = useRef(null);
  const timetickTimerReset: any = useRef(null);

  useEffect(() => {
    try {
      const xapi = XAPIService.getInstance();
      const sessionState = xapi.initialize();
      sessionState.then(() => {
        if (!xapi.available()) return;
        const authToken = xapi.cmi5.getAuthToken();
        if (authToken) {
          const launchParameters = xapi.cmi5.getLaunchParameters();
          launchParameters.authToken = authToken;
          axios.put(`${config.backend}/follow/${courseId}/${authUser.id}`, {
            xapidetails: JSON.stringify(launchParameters),
          });
          localStorage.setItem('xapitoken', authToken);
        }
      });
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    timetickTimerReset.current = () => {
      clearInterval(timetickTimer.current);
      const activeLesson = activeLessonRef?.current as any;

      // Guard: can be null/{} when switching panes etc.
      if (!activeLesson?.lessonId) return;

      const delay = activeLesson?.timed === 'default' ? 60 : 5; //TODO

      // Fire an immediate “tick” so BE can set courseTimerStartedAt right away
      (async () => {
        if (didImmediateTickRef.current) return;
        didImmediateTickRef.current = true;
        const activeLesson = activeLessonRef?.current as any;
        const newStatuses = deep_copy(statusRef?.current);
        const i = newStatuses?.findIndex(
          (x: any) => x?.lessonId === activeLesson?.lessonId
        );
        const status = newStatuses?.[i];

        if (status?.id) {
          await axios.put(`${config.backend}/follows/timetick/${status.id}`, {
            seconds: 1,
            userId: authUser?.id,
          });
        }
      })().catch(console.warn);

      timetickTimer.current = setInterval(async () => {
        //!important functions inside intervaal do not have access to regular state
        const activeLesson = activeLessonRef?.current as any;
        const newStatuses = deep_copy(statusRef?.current);
        const i = newStatuses?.findIndex(
          (x: any) => x?.lessonId === activeLesson?.lessonId
        );
        const status = newStatuses?.[i];
        setLastTickTime(new Date());

        if (status?.id) {
          await axios.put(`${config.backend}/follows/timetick/${status.id}`, {
            seconds: delay,
            userId: authUser?.id,
          });
        }
      }, delay * 1000);
    };
    return () => {
      clearInterval(timetickTimer.current);
    };
  }, []);

  const userId = studentId ? studentId : auth?.user?.id;

  // Select the status before rendering LessonView. Updating these refs in an
  // effect lets a newly selected lesson render with the previous lesson's status
  // (including its pinned content URL), and a ref update alone cannot fix that render.
  activeStatus.current = (activeLesson as any)?.lessonId
    ? statuses?.find(
        (status: any) => status?.lessonId == (activeLesson as any)?.lessonId
      ) || null
    : null;
  activeStatusAsset.current = (activeLesson as any)?.assetId
    ? statusesAssets?.find(
        (status: any) => status?.assetId == (activeLesson as any)?.assetId
      ) || null
    : null;

  const showContent = !isTeacher || activeStatus.current?.status;
  const hideScore = !isTeacher && course?.courseType === 'assessment';

  useEffect(() => {
    if (activeLesson && (student || !expectAStudent)) {
      if (!setLoadedRef.current) setLoaded(newUid(20));
    }
  }, [statuses, statusesAssets, activeLesson, student]);

  useEffect(() => {
    if (loaded) {
      enterLesson();
    }
  }, [loaded]);

  useEffect(() => {
    setNavDisabled(true);
    return () => setNavDisabled(false);
  }, [setNavDisabled]);

  useEffect(() => {
    if (showExtraInfo) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [showExtraInfo]);

  useEffect(() => {
    hideContent && setActiveLesson({} as any);
    setRefTree(newUid(10));
  }, [hideContent]);

  useEffect(() => {
    if ((activeLesson as any)?.assetId) {
      setLoadeContent(true);
      setSubmitFn(null);
    }
    if (divRef.current) {
      setIsLessonLoaded(false);
    }
  }, [activeLesson]);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const scrollHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = window.scrollY;
      const persentage = (scrolled / scrollHeight) * 100;
      setScrollPercentage(persentage);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (loaded) leaveLesson();
    };
  }, []);

  useGet(`/coursedetail/${courseId}`, {
    retry: false,
    onSuccess: (result: any) => {
      if (result?.data?.startsWith?.('scorm')) {
        setIsScormScourse(true);
        const basePath = window.location.pathname.slice(
          0,
          nthIndexOf(window.location.pathname, '/', 3)
        );
        return navigate(`${basePath}/scormCourses/${result?.data?.id}`);
      }
      setCourse(result?.data || {});
    },
    onError: (res: any) => {
      navigate(`/${i18n.language}/noaccess`);
    },
  });

  const [isFollow, setIsFollow] = useState(false);

  useGet(`/follow/${courseId}/${auth?.user?.id}`, {
    retry: false,
    onSuccess: (result: any) => {
      setIsFollow(true);

      const currentUrl = new URL(window.location.href);

      if (currentUrl.searchParams.has('qr_code')) {
        currentUrl.searchParams.delete('qr_code');
        window.history.replaceState(null, '', currentUrl.toString());
      }
    },
    onError: (res: any) => {
      if (isTeacher) {
        setIsFollow(true);
      }
      if (qr_share) {
        navigate(basePath.slice(0, basePath.lastIndexOf('/les')));
      }
    },
  });

  const handleSetSubmitFn = useCallback(
    (fn: any) => {
      setSubmitFn(() => fn);

      if (
        activeStatus?.current &&
        activeStatus?.current?.status === 'completed' &&
        fn
      ) {
        axios
          .put(
            `${config.backend}/lessonstatus/${courseId}/${userId}/${
              (activeLesson as any)?.lessonId
            }`,
            {
              courseId: activeStatus?.current?.courseId,
              userId: activeStatus?.current?.userId,
              status: 'started',
            }
          )
          .then((result) => {
            updateStatus(result?.data);
          });
      }

      const practicalWidgets = document?.querySelectorAll('.widget_practical');
      const nextHasWidgets = practicalWidgets?.length > 0;

      setHasWidgets((prev) =>
        prev === nextHasWidgets ? prev : nextHasWidgets
      );
    },
    [courseId, userId, activeLesson]
  );

  const handleSetNrOfPages = useCallback((nextNrOfPages: number) => {
    setPage((prev) => (prev === 0 ? prev : 0));
    setNrOfPages((prev) => (prev === nextNrOfPages ? prev : nextNrOfPages));
  }, []);

  const { refetch: refetchStudentInfo } = useGet(
    `/student/${courseId}/${userId}`,
    {
      enabled: !!userId && isFollow,
      onSuccess: async (result: any) => {
        const xapi = XAPIService.getInstance();
        const scorm = ScormService.getInstance();
        const tincan = TinCanService.getInstance();
        const lti = LtiService.getInstance({
          courseId,
          userId,
          progressStep: 0.01, // fire backend every 1-percentage change
        });
        const newFollow = result?.data;
        const isComplete =
          newFollow.progress >= 0.99 && newFollow.tasksRemaining === 0;

        if (lti.available()) {
          await (isComplete
            ? lti.reportFinal()
            : lti.progress(newFollow.progress));
        }

        try {
          if (scorm.available()) {
            isComplete
              ? scorm.reportFinal(newFollow, course)
              : scorm.progress(newFollow.progress, newFollow.totalTimeSpent);
          }
        } catch (e) {
          console.warn(e);
        }

        try {
          xapi.initialize();
          await xapi.initializedPromise;
          if (xapi.available()) {
            isComplete
              ? xapi.reportFinal(newFollow, course)
              : xapi.progress(newFollow.progress);
          }
        } catch (e) {
          console.error(e);
        }

        try {
          tincan.reportStatus(newFollow, course);
        } catch (e) {
          console.error(e);
        }

        setstudent(newFollow || {});
      },
    }
  );

  // Initial load (and any hook-driven refreshes) populate state via onSuccess.
  useGet(`/lessonstatuses`, {
    courseId: courseId,
    userId: userId,
    enabled: !isPreview,
    onSuccess: (result: any) => setStatuses(result?.data),
  });

  useGet(`/assetstatuses`, {
    courseId: courseId,
    userId: userId,
    enabled: !isPreview,
    onSuccess: (result: any) => setStatusesAssets(result?.data),
  });

  // ✅ Explicit reload (don’t rely on useGet refetch calling onSuccess)
  const reloadStatuses = async () => {
    if (!courseId || !userId || isPreview) return;

    const [ls, as] = await Promise.all([
      axios.get(`${config.backend}/lessonstatuses`, {
        params: { courseId, userId },
      }),
      axios.get(`${config.backend}/assetstatuses`, {
        params: { courseId, userId },
      }),
    ]);

    // Force new array references to trigger downstream effects
    const lessonData = Array.isArray(ls?.data) ? [...ls.data] : ls?.data;
    const assetData = Array.isArray(as?.data) ? [...as.data] : as?.data;

    setStatuses(lessonData);
    setStatusesAssets(assetData);

    // Optional but helps if LessonTree caches derived state internally
    setRefTree(newUid(10));
  };

  const handleSidebar = () => {
    setHideSidebar(!hideSidebar);
  };

  useEffect(() => {
    if (activeLesson) {
      setIsLessonLoadedSub(false);
    }
  }, [activeLesson]);

  const handleCourseExpire = async () => {
    if (courseExpireRunning) return;
    if (isTeacher || isPreview) return;

    setCourseExpireRunning(true);
    try {
      setCourseExpiredLocal(true);

      // 1) submit current lesson WITHOUT navigating (must bypass expired guard)
      await submitCore({ navigateAfter: false, allowWhenExpired: true }, true);

      // 2) bulk autosubmit remaining results
      await axios.post(
        `${config.teodoc}/corrections/autosubmit/${courseId}/${userId}`
      );

      // 3) refresh FE state that drives UI (student + BOTH status lists)
      await Promise.all([refetchStudentInfo(), reloadStatuses()]);

      // 4) navigate once
      if (nextLesson) {
        gotoLesson(nextLesson, false);
      } else {
        setShowCourseSuccess(true);
      }
    } catch (e) {
      console.error(e);
      setShowNotification(t('notifications.lesson_submit_failed'));
    } finally {
      setCourseExpireRunning(false);
    }
  };

  const handleLessonExpire = async () => {
    if (lessonExpireRunning) return;
    if (isTeacher || isPreview) return;

    setLessonExpireRunning(true);
    try {
      setLessonExpiredLocal(true);
      clearInterval(timetickTimer.current);

      await submitCore({ navigateAfter: false, allowWhenExpired: true }, true);

      await Promise.all([refetchStudentInfo(), reloadStatuses()]);
    } catch (e) {
      console.error(e);
      setShowNotification(t('notifications.lesson_submit_failed'));
    } finally {
      setLessonExpireRunning(false);
    }
  };

  const handleReplaceParam = async (lesson: any) => {
    const searchString = 'lesson/' + lessonId;
    const replacementString = 'lesson/' + lesson;
    const modifiedString = basePath.replace(searchString, replacementString);
    if (lesson) {
      navigate({
        pathname: lesson ? modifiedString : window.location.pathname,
        search: window.location.search,
      });
    } else {
      setShowCourseSuccess(true);
    }
  };

  const submitCore = async (
    {
      navigateAfter,
      allowWhenExpired = false,
    }: { navigateAfter: boolean; allowWhenExpired?: boolean },
    force = false,
    widget: any = undefined
  ) => {
    if (
      !allowWhenExpired &&
      (courseTimerExpired || lessonTimerExpired) &&
      !isTeacher &&
      !isPreview
    )
      return;

    if (!submitFnRef.current) return;

    setIsLessonLoadedSub(true);
    setIsIncomplete(false);

    const result = await submitFnRef.current(true, force, widget);

    if (result && result?.status === 'incomplete') {
      try {
        scrollIntoViewWithOffset(`#${result?.uid}`, 100);
      } catch (e) {
        console.error(e);
      }
      setIsIncomplete(true);
      setIncompleteWidgets(result);
      setIsLessonLoadedSub(false);
      return { ok: false, incomplete: true, result };
    }

    if (!result) {
      console.error(result);
      setShowNotification(t('notifications.lesson_submit_failed'));
      setIsLessonLoadedSub(false);
      return { ok: false, failed: true };
    }

    setShowNotification(t('notifications.lesson_submitted'));

    await axios
      .get(`${config.backend}/lessonstatus/${courseId}/${userId}/${lessonId}`)
      .then(async (res) => {
        await updateStatus(res?.data);
        return res?.data;
      });

    if (expectAStatus) {
      const status = activeStatus?.current;
      if (status?.id) {
        await axios.put(`${config.backend}/follows/timetick/${status.id}`, {
          seconds: Math.floor((Date.now() - lastTickTime.getTime()) / 1000),
          userId: authUser?.id,
        });
      }

      setLastTickTime(new Date());
      clearInterval(timetickTimer.current);
    }

    if (navigateAfter) {
      gotoLesson(nextLesson, false);
    } else {
      // keep UI consistent: submission overlay off
      setIsLessonLoadedSub(false);
    }

    return { ok: true };
  };

  // keep your existing public submit() API for normal clicks
  const submit = (force = false, widget: any = undefined) => {
    submitCore({ navigateAfter: true }, force, widget);
  };

  const gotoLesson = async (lessson: any, autoSave = true) => {
    setIsLessonLoaded(true);
    if (loaded && lessson?.id !== (activeLesson as any)?.lessonId && autoSave) {
      await leaveLesson();
    }
    setLoaded(null);
    refetchStudentInfo();
    handleReplaceParam(lessson);
  };

  useContentLinks({
    onLesson: (target) => gotoLesson(target.lessonId || target.assetId),
    onExtraInfo: (target) => {
      const matches = ((allExtraInfo as any) || []).filter((x: any) =>
        matchesContentLink(x, target)
      );
      const item =
        matches.find(
          (x: any) => x?.rootLessonId == (activeLesson as any)?.lessonId
        ) || matches[0];
      if (!item) return;
      setActiveExtraInfo(item);
      setShowExtraInfo(true);
    },
  });

  const updateStatus = (status: any) => {
    const newStatuses = deep_copy(statusRef?.current);
    const i = newStatuses?.findIndex(
      (x: any) => x?.lessonId == status?.lessonId
    );
    if (i !== -1) {
      newStatuses[i] = status;
    } else {
      newStatuses.push(status);
    }
    setStatuses(newStatuses);
  };

  const updateStatusAssets = (status: any) => {
    const newStatuses = deep_copy(statusesAssetsRef?.current);
    const i = newStatuses?.findIndex((x: any) => x?.assetId == status?.assetId);
    if (i !== -1) {
      newStatuses[i] = status;
    } else {
      newStatuses.push(status);
    }
    setStatusesAssets(newStatuses);
  };

  const enterLesson = async () => {
    const activeLesson = activeLessonRef?.current as any;
    const status = activeStatus?.current;
    const statusAsset = activeStatusAsset?.current;

    const lessonFinished =
      status?.status === 'corrected' ||
      status?.status === 'submitted' ||
      status?.status === 'completed';

    clearInterval(timetickTimer.current);
    setLastTickTime(new Date());

    window.scrollTo(0, 0);
    setScrollPercentage(0);

    if (
      (activeLesson?.lessonId || activeLesson?.assetId) &&
      student &&
      !isTeacher
    ) {
      axios.put(`${config.backend}/follow/${courseId}/${userId}`, {
        courseId: courseId,
        currentLessonId: activeLesson.lessonId || activeLesson?.assetId,
        userId: userId,
      });

      if (!status && expectAStatus && activeLesson?.lessonId) {
        // Can race LessonHeader's own (timer-driven) POST to the same
        // endpoint for the same lessonstatuses row — whichever insert wins
        // the unique constraint sticks, and the other throws. Both sides
        // now send `version`, and this one shouldn't take down enterLesson
        // if it loses the race.
        await axios
          .post(`${config.backend}/lessonstatuses`, {
            status: 'started',
            version: activeLesson?.version,
            coursecontentId: course?.coursecontentId,
            courseId: courseId,
            userId: userId,
            lessonId: activeLesson?.lessonId,
          })
          .then((result) => {
            if (result?.data) updateStatus(result?.data);
          })
          .catch((e) =>
            console.error('[LessonPage] enterLesson status POST failed', e)
          );
      }

      if (!statusAsset && expectAStatus && activeLesson?.assetId) {
        await axios
          .post(`${config.backend}/assetstatuses`, {
            status: 'started',
            coursecontentId: course?.coursecontentId,
            courseId: courseId,
            userId: userId,
            assetId: activeLesson?.assetId,
          })
          .then((result) => {
            if (result?.data) updateStatusAssets(result?.data);
          });
      }

      if (!lessonFinished && !lessonTimerExpired && expectAStatus) {
        timetickTimerReset.current();
      }
    }
  };

  const submitDisabled =
    activeStatus?.current?.status === 'corrected' ||
    activeStatus?.current?.status === 'submitted' ||
    (!isTeacher && (activeLesson as any)?.submitByTeacher) ||
    ((courseTimerExpired || lessonTimerExpired) && !isTeacher && !isPreview);

  const submitDisabledReasonKey = (() => {
    if (!submitDisabled) return undefined;
    if (isPreview) return undefined;

    if (
      activeStatus?.current?.status === 'submitted' ||
      activeStatus?.current?.status === 'corrected'
    ) {
      return 'pages.course_landing.submit_unavailable';
    }

    if (!isTeacher && (activeLesson as any)?.submitByTeacher) {
      return 'pages.course_landing.submit_unavailable_by_teacher';
    }

    if (!isTeacher && (courseTimerExpired || lessonTimerExpired)) {
      return 'pages.course_landing.submit_unavailable_time_expired';
    }

    return undefined;
  })();

  const leaveLesson = async () => {
    setLoaded(null);
    const status = activeStatus?.current;
    const statusAsset = activeStatusAsset?.current;

    if ((activeLesson as any)?.lessonId) {
      if (!status) return;

      const lessonFinished =
        status?.status === 'corrected' ||
        status?.status === 'submitted' ||
        status?.status === 'completed';

      clearInterval(timetickTimer.current);
      if (!lessonFinished && expectAStatus) {
        if (status.id) {
          axios.put(`${config.backend}/follows/timetick/${status.id}`, {
            seconds: Math.floor((Date.now() - lastTickTime.getTime()) / 1000),
            userId: authUser?.id,
          });
        }
      }
      setLastTickTime(new Date());

      const submitDisabled =
        status?.status === 'corrected' ||
        status?.status === 'submitted' ||
        ((courseTimerExpired || lessonTimerExpired) &&
          !isTeacher &&
          !isPreview);

      if (submitFn && !lessonFinished && !submitDisabled) {
        await submitFnRef.current(false);
      }

      if (
        status &&
        status?.status === 'started' &&
        !submitFn &&
        expectAStatus
      ) {
        const result = await axios.put(
          `${config.backend}/lessonstatus/${courseId}/${userId}/${
            (activeLesson as any)?.lessonId
          }`,
          {
            courseId: status?.courseId,
            userId: status?.userId,
            status: 'completed',
          }
        );
        updateStatus(result?.data);
      }
    }

    if ((activeLesson as any)?.assetId) {
      if (!statusAsset) return;

      if (
        statusAsset &&
        statusAsset?.status === 'started' &&
        !submitFn &&
        expectAStatus
      ) {
        const result = await axios.put(
          `${config.backend}/assetstatus/${courseId}/${userId}/${
            (activeLesson as any)?.assetId
          }`,
          {
            assetId: (activeLesson as any)?.assetId,
            courseId: courseId,
            userId: userId,
            status: 'completed',
          }
        );
        updateStatusAssets(result?.data);
      }
    }
  };

  const timeSpent = activeStatus?.current?.timeSpent;

  if (showCourseSuccess) {
    return (
      <CourseSuccess
        onClose={() => setShowCourseSuccess(false)}
        studentId={studentId || (userId as any)}
        course={course}
      />
    );
  }

  const lessonFinished =
    activeStatus?.current?.status === 'corrected' ||
    activeStatus?.current?.status === 'submitted' ||
    activeStatus?.current?.status === 'completed';

  const isLastPage = page == nrOfPages - 1;
  const isLastLesson = !nextLesson;

  return (
    <div className="flex w-full flex-col lg:min-h-[calc(100vh_-_70px)]">
      {!loadeContent && result && (
        <div className="fixed inset-0 z-[12] flex bg-white">
          <div className="m-auto">
            <TEOLoader
              loaderSrc="/images/TEOloader.gif"
              className="w-[110px]"
            />
          </div>
        </div>
      )}
      {!statuses && !isPreview ? null : (
        <>
          {(!hideContent || !isMobile) && (!showExtraInfo || !isMobile) && (
            <>
              <LessonHeader
                isPreview={isPreview}
                coursePath={coursePath}
                isTeacher={isTeacher}
                student={student}
                course={course}
                setHideContent={setHideContent}
                hideContent={hideContent}
                timeDisabled={lessonFinished || lessonTimerExpired}
                activeLesson={activeLesson}
                timeSpent={timeSpent}
                userId={userId}
                submitFn={() => submit(true)}
                onCourseExpire={handleCourseExpire}
                onLessonExpire={handleLessonExpire}
                nextLesson={nextLesson}
                activeStatus={activeStatus?.current}
                setShowExtraInfo={setShowExtraInfo}
                setExtraInfoFullScreen={setExtraInfoFullScreen}
                extraInfoFullScreen={extraInfoFullScreen}
                setActiveExtraInfo={setActiveExtraInfo}
                lengthExtraInfo={lengthExtraInfo}
                iSsmallLaptop={iSsmallLaptop}
                setCourseExpiredLocal={setCourseExpiredLocal}
              />
              {notification && <NotificationMessage />}
              <div
                className="progress-bar"
                style={{
                  width: `${scrollPercentage}%`,
                  height: '3px',
                  backgroundColor:
                    scrollPercentage > 99 ? '#42e466' : '#3257fc',
                  position: 'fixed',
                  top: '80px',
                  zIndex: 12,
                }}
              ></div>
            </>
          )}
          <div className="flex grow">
            <div
              className={`left-0 flex w-full flex-col border-r border-solid border-grey-02 p-5 px-2 duration-500 ease-out md:!max-w-[380px]
          ${hideSidebar && !isMobile ? '!absolute !-left-full' : null} ${
                isMobile && !hideContent ? '!hidden' : null
              }`}
            >
              <div className="p-3 pt-0 sm:p-5 sm:pt-0 md:sticky md:top-[100px] md:max-h-[calc(100vh_-_100px)] md:w-[380px] md:overflow-y-auto">
                <div className={`flex w-full items-center`}>
                  <p
                    className={`mt-2 mb-4 pl-2 text-base font-medium ${
                      isMobile && 'mx-auto mb-6'
                    }`}
                  >
                    {t('pages.course_landing.contents')}
                  </p>
                  {hideContent && isMobile && (
                    <IconButton
                      Icon={Cross}
                      variant="ghost"
                      className="-mr-1 mb-4 p-2.5"
                      onClick={() => {
                        setHideContent(false);
                      }}
                    />
                  )}
                </div>

                {isMobile && (
                  <div className="flex items-center px-2 pb-4">
                    <div className="mr-3 w-16 min-w-16">
                      <img
                        src={course?.image || '/images/courseFallback.png'}
                        alt={course?.name}
                        className="inline h-12 w-full rounded-lg object-cover"
                      />
                    </div>
                    <p className="text-sm font-medium text-black">
                      {course?.name}
                    </p>
                    {student ? (
                      <div
                        className="relative ml-auto h-8 w-8 min-w-[32px] rounded-full text-center"
                        style={{
                          background: `radial-gradient(closest-side, white 85%, transparent 90% 100%),conic-gradient(#3257fc ${
                            student?.progress
                              ? Math.round(student.progress * 100)
                              : 0
                          }%, #1e1e2014 0)`,
                        }}
                      >
                        <p className="pt-2 text-xs text-grey-07">
                          {student?.progress
                            ? Math.round(student.progress * 100)
                            : 0}
                          &#37;
                        </p>
                      </div>
                    ) : null}
                  </div>
                )}

                {course && (
                  <LessonTree
                    key={refTree}
                    hideScore={hideScore}
                    coursecontentId={
                      student?.coursecontentId || course?.coursecontentId
                    }
                    courseId={student?.coursecontentId ? undefined : courseId}
                    userId={userId}
                    activeLesson={activeLesson}
                    courseOrder={course?.inOrder}
                    rowPath={basePath}
                    setActiveLesson={async (newActiveLesson: any) => {
                      if (
                        loaded &&
                        newActiveLesson?.lessonId !==
                          (activeLesson as any)?.lessonId
                      ) {
                        leaveLesson();
                        setLoaded(null);
                      }
                      setActiveLesson(newActiveLesson);
                    }}
                    onFirstLesson={async (node: any) => {
                      if (
                        (!activeLesson && node?.lessonId) ||
                        (!activeLesson && node?.assetId)
                      ) {
                        const prev = prevPage ? '&details=' + prevPage : '';
                        let search = window.location.search + prev;
                        let myStudent: any;

                        if (!student) {
                          await axios
                            .get(
                              `${config.backend}/follow/${course.id}/${authUser.id}`
                            )
                            .then((result) => {
                              myStudent = result.data;
                            });
                        } else {
                          myStudent = student;
                        }

                        const searchString = lessonId
                          ? 'lesson/' + lessonId
                          : 'lesson';
                        const replacementString =
                          'lesson/' +
                          (myStudent?.currentLessonId ||
                            node?.lessonId ||
                            node?.assetId);

                        const modifiedString = basePath.replace(
                          searchString,
                          replacementString
                        );

                        if (search[0] === '&')
                          search = '?' + search.substring(1);

                        navigate({
                          pathname: modifiedString,
                          search: search,
                        });
                      }
                    }}
                    setHideContent={setHideContent}
                    setNextLesson={setNextLesson}
                    setPreviousLesson={setPreviousLesson}
                    isUserFollow={expectAStudent}
                    isTeacher={isTeacher}
                    status={statuses}
                    statusAsset={statusesAssets}
                    setAllExtraInfo={setAllExtraInfo}
                    setRootCourseId={setRootCourseId}
                    setAllDirectories={setAllDirectories}
                    setAllAssets={setAllAssets}
                    setNumLesAs={setNumLesAs}
                    setIsLessonLoaded={setIsLessonLoaded}
                  />
                )}
              </div>
            </div>

            <div className={`w-full ${isMobile && hideContent && '!hidden'}`}>
              <div className="relative flex h-full grow p-4 ">
                {(isLessonLoaded || isLessonLoadedSub) && !isTeacher && (
                  <div className="absolute inset-0 z-[3] flex flex bg-white/75">
                    <div className="relative mx-auto">
                      <div className="sticky top-1/2 m-auto h-[110px]">
                        <TEOLoader
                          loaderSrc="/images/TEOloader.gif"
                          className="-mt-[60px] w-[110px]"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="fr-view mx-auto w-full max-w-2xl">
                  {/* Confine lesson HTML to its own stacking context so even
                      high-z-index content cannot cover navigation controls. */}
                  <div className="relative z-0">
                    {activeLesson &&
                      (student || isPreview) &&
                      ((activeLesson as any)?.lessonId ? (
                        <LessonView
                          key={(activeLesson as any).id}
                          node={activeLesson}
                          course={course}
                          student={student}
                          isTeacher={isTeacher}
                          status={activeStatus?.current}
                          page={page}
                          setNrOfPages={handleSetNrOfPages}
                          setSubmitFn={handleSetSubmitFn}
                          onSubmit={async (submitData: any) => {
                            if (submitData.allSubmitted) {
                              const isCorrected = Object.values(
                                submitData?.widgets
                              ).some(
                                (widget: any) =>
                                  widget?.state?.type === 'OpenVraag' ||
                                  widget?.state?.type === 'FileUpload'
                              )
                                ? 'submitted'
                                : 'corrected';

                              axios
                                .put(
                                  `${
                                    config.backend
                                  }/lessonstatus/${courseId}/${userId}/${
                                    (activeLesson as any)?.lessonId
                                  }`,
                                  {
                                    courseId: activeStatus?.current?.courseId,
                                    userId: activeStatus?.current?.userId,
                                    status: isCorrected,
                                  }
                                )
                                .then((result) => {
                                  updateStatus(result?.data);
                                  refetchStudentInfo();
                                });
                            }
                          }}
                          timeDisabled={lessonFinished || lessonTimerExpired}
                          setLoadeContent={setLoadeContent}
                          nodeGetData={
                            lessonFinished &&
                            activeStatus?.current?.getData &&
                            activeStatus?.current?.status !== 'completed' &&
                            activeStatus?.current?.version !==
                              (activeLesson as any)?.version
                              ? activeStatus?.current.getData
                              : (activeLesson as any)?.getData
                          }
                          setIsLoaded={setIsLessonLoaded}
                        ></LessonView>
                      ) : (
                        (activeLesson as any)?.assetId &&
                        (activeLesson as any)?.assetPath && (
                          <div className="flex w-full items-start p-3">
                            {(activeLesson as any)?.assetType == 'image' ? (
                              <ImageZoom
                                activeExtraInfo={activeLesson}
                                extraInfoFullScreen={true}
                                setImageZoom={setImageZoom}
                                imageZoom={imageZoom}
                                onLoad={() => {
                                  setIsLessonLoaded(false);
                                }}
                              />
                            ) : (
                              <>
                                {(activeLesson as any)?.assetType == 'video' ? (
                                  <div
                                    id={`${newUid(20)}_embed`}
                                    className="mx-auto"
                                  >
                                    <iframe
                                      id={`${newUid(20)}_placeholder`}
                                      width="640"
                                      height="360"
                                      className=""
                                      src={(activeLesson as any)?.assetPath}
                                      allowFullScreen
                                      onLoad={() => {
                                        setIsLessonLoaded(false);
                                      }}
                                    ></iframe>
                                  </div>
                                ) : (
                                  <div
                                    ref={divRef}
                                    id={`${newUid(20)}_embed`}
                                    className="mx-auto"
                                    dangerouslySetInnerHTML={{
                                      __html: (activeLesson as any)?.assetPath,
                                    }}
                                  />
                                )}
                              </>
                            )}
                          </div>
                        )
                      ))}
                  </div>

                  {showContent && (
                    <div className="relative z-[1] mt-6 flex w-full justify-between">
                      <div>
                        {previousLesson && (
                          <Button
                            onClick={() => gotoLesson(previousLesson)}
                            variant="secondary"
                            disabled={
                              blockForLessonTimer ||
                              course?.courseStatus === 'disabled'
                            }
                          >
                            {t('pages.course_landing.previous')}
                          </Button>
                        )}
                        {page > 0 && (
                          <Button
                            disabled={course?.courseStatus === 'disabled'}
                            onClick={async () => {
                              setPage(page - 1);
                              window.scrollTo(0, 0);
                            }}
                          >
                            {t('pages.course_landing.previous_page')}
                          </Button>
                        )}
                      </div>

                      <div className="flex gap-2">
                        {page < nrOfPages - 1 && (
                          <Button
                            disabled={course?.courseStatus === 'disabled'}
                            name="next_page"
                            onClick={async () => {
                              setPage(page + 1);
                              window.scrollTo(0, 0);
                            }}
                          >
                            {t('pages.course_landing.next_page')}
                          </Button>
                        )}

                        {submitFn &&
                          authUser &&
                          !isPreview &&
                          isLastPage &&
                          !isLastLesson &&
                          course?.correctionType !== 'autofeedback' && (
                            <Button
                              disabled={
                                submitDisabled ||
                                course?.courseStatus === 'disabled'
                              }
                              title={
                                submitDisabledReasonKey &&
                                t(submitDisabledReasonKey)
                              }
                              onClick={() => {
                                submit();
                              }}
                            >
                              {t('pages.course_landing.submit')}
                            </Button>
                          )}

                        {(isPreview ||
                          !submitFn ||
                          course?.correctionType === 'autofeedback') &&
                          nextLesson &&
                          page == nrOfPages - 1 && (
                            <Button
                              name="next_lesson"
                              disabled={
                                blockForLessonTimer ||
                                course?.courseStatus === 'disabled'
                              }
                              onClick={async () => {
                                if (
                                  course?.correctionType === 'autofeedback' &&
                                  !submitDisabled &&
                                  hasWidgets &&
                                  !(
                                    (courseTimerExpired ||
                                      lessonTimerExpired) &&
                                    !isTeacher &&
                                    !isPreview
                                  )
                                ) {
                                  submit();
                                } else {
                                  gotoLesson(nextLesson);
                                }
                              }}
                            >
                              {t('pages.course_landing.next')}
                            </Button>
                          )}

                        {authUser &&
                          !isPreview &&
                          isLastLesson &&
                          isLastPage && (
                            <Button
                              disabled={
                                ((courseTimerExpired || lessonTimerExpired) &&
                                  !isTeacher &&
                                  !isPreview) ||
                                isLessonLoadedSub ||
                                (course?.courseStatus === 'disabled' &&
                                  !isTeacher)
                              }
                              title={
                                (courseTimerExpired || lessonTimerExpired) &&
                                !isTeacher &&
                                !isPreview
                                  ? t(
                                      'pages.course_landing.submit_unavailable_time_expired'
                                    )
                                  : course?.courseStatus === 'disabled' &&
                                    !isTeacher
                                  ? t(
                                      'pages.course_landing.submit_unavailable_course_disabled'
                                    )
                                  : undefined
                              }
                              onClick={async () => {
                                setIsLessonLoaded(true);

                                if (submitFnRef.current && !submitDisabled) {
                                  const res = await submitCore({
                                    navigateAfter: false,
                                  });
                                  if (!res?.ok) {
                                    setIsLessonLoaded(false);
                                    return;
                                  }
                                } else {
                                  await leaveLesson();
                                }

                                setShowCourseSuccess(true);
                              }}
                            >
                              {t('pages.course_landing.finish')}
                            </Button>
                          )}
                      </div>
                    </div>
                  )}
                </div>

                {!(activeLesson as any)?.assetId &&
                  (activeLesson as any)?.lessonType !== 'instruction' && (
                    <div className={` ${!iSsmallLaptop && 'pl-3'}`}>
                      <ExtraInfoLesson
                        node={activeLesson}
                        course={course}
                        rootCourseId={rootCourseId}
                        allExtraInfo={allExtraInfo}
                        allDirectories={allDirectories}
                        setShowExtraInfo={setShowExtraInfo}
                        showExtraInfo={showExtraInfo}
                        setExtraInfoFullScreen={setExtraInfoFullScreen}
                        extraInfoFullScreen={extraInfoFullScreen}
                        setActiveExtraInfo={setActiveExtraInfo}
                        activeExtraInfo={activeExtraInfo}
                        setLengthExtraInfo={setLengthExtraInfo}
                        lengthExtraInfo={lengthExtraInfo}
                        preview={false}
                        iSsmallLaptop={iSsmallLaptop}
                        isMobile={isMobile}
                        viewMobile={false}
                      />
                    </div>
                  )}
              </div>
            </div>
          </div>
        </>
      )}

      <div data-testid="hide-sidebar" className="hidden md:block">
        <IconButton
          className="fixed left-2 bottom-5 z-[4] bg-white hover:!bg-[#f4f4f4] "
          Icon={Sidebar}
          variant="outline"
          onClick={handleSidebar}
        />
      </div>

      {isIncomplete && (
        <IncompleteModal
          setIsIncomplete={setIsIncomplete}
          submit={submit}
          incompleteWidgets={incompleteWidgets}
          setIsLessonLoadedSub={setIsLessonLoadedSub}
        />
      )}
    </div>
  );
}

export default LessonPage;
